/**
 * Point Draw geometry engine (pure).
 *
 * A Point-Draw track is a connected run of axis-aligned (horizontal/vertical)
 * straight rails joined by fixed-radius 90° corner curves. This module holds the
 * pure geometry used while *drawing* a run — expressing connections and loop
 * closures as a corner-vertex polyline, cleaning it into a legal Manhattan
 * path/loop, and deciding where curves may be inserted.
 *
 * Once a run is committed it is just a set of ordinary `overhead-rail`
 * LayoutElements with no special tag or editing model: committed Point-Draw
 * tracks are edited by the exact same CanvasElement pipeline as drag-created
 * tracks (select, move, rotate, resize, delete, multi-select, copy/paste).
 */

export interface Vec {
  x: number;
  y: number;
}

// ── Manhattan connection / cleanup engine ───────────────────────────────────
//
// Every Point Draw connection and loop closure is expressed as a corner-vertex
// polyline, cleaned up, and then regenerated into primitives by the shared
// generator. Because the generator only ever emits horizontal/vertical straights
// and fixed-radius 90° curves — with a mandatory tangent straight between any two
// curves — illegal geometry (back-to-back curves, S-connectors, curve-into-curve,
// stretched curves, tiny fillers) is structurally impossible.

/** Dominant-axis heading (0=E, 90=S, 180=W, 270=N) of the segment a→b. */
export function segHeading(a: Vec, b: Vec): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 0 : 180;
  return dy >= 0 ? 90 : 270;
}

/** True when a→b runs purely along one axis (and has non-zero length). */
export function isAxisAligned(a: Vec, b: Vec): boolean {
  const dx = Math.abs(b.x - a.x);
  const dy = Math.abs(b.y - a.y);
  return (dx <= 1e-6 && dy > 1e-6) || (dy <= 1e-6 && dx > 1e-6);
}

export interface CleanupResult {
  vertices: Vec[];
  valid: boolean;
}

/** Vertex a run would close back onto, plus its index in the vertex list. */
export interface CloseAnchor {
  index: number;
  point: Vec;
}

/**
 * Smallest number of corners that can bound a closed Manhattan loop.
 *
 * Three vertices already suffice: the closure leg supplies the fourth corner of
 * the rectangle, so a run only needs three committed corners past the anchor
 * before it can be shut.
 */
const MIN_LOOP_VERTICES = 3;

/**
 * Vertex the run should close onto for a click at `point`, or null when the
 * click is not a closure.
 *
 * Deliberately searches the whole prefix rather than only `vertices[0]`. A loop
 * is frequently reached by first branching off existing track, which spends the
 * opening vertices on a lead-in stub — the corner where the loop actually meets
 * itself is then somewhere in the middle of the list, and anchoring on the run's
 * first vertex measures the closure against a point that can sit an arbitrary
 * distance away. Every vertex from the head backwards is a legal anchor as long
 * as enough corners remain after it to bound a loop.
 *
 * Nearest wins so that a run doubling back through its own start still closes on
 * the corner under the cursor. Ties resolve to the earlier vertex, which yields
 * the larger loop and the shorter leftover tail.
 */
export function resolvePointDrawCloseAnchor(
  vertices: Vec[],
  point: Vec,
  tolerance: number
): CloseAnchor | null {
  let best: CloseAnchor | null = null;
  let bestDist = Infinity;
  for (let i = 0; i <= vertices.length - MIN_LOOP_VERTICES; i++) {
    const d = Math.hypot(vertices[i].x - point.x, vertices[i].y - point.y);
    if (d > tolerance || d >= bestDist) continue;
    bestDist = d;
    best = { index: i, point: vertices[i] };
  }
  return best;
}

/**
 * Manhattan corners joining the head back to the anchor, as ordered candidates.
 *
 * The anchor itself is omitted: it already opens the loop, so repeating it would
 * read as a coincident vertex. Both leg orderings are offered because only one
 * of them may leave room for the corner fillets; the caller takes the first that
 * the generator accepts. An empty candidate list means head and anchor coincide,
 * which is not a closure the caller can build a loop from.
 */
export function closureApproachCandidates(anchor: Vec, head: Vec): Vec[][] {
  const dxSame = Math.abs(head.x - anchor.x) < 1e-6;
  const dySame = Math.abs(head.y - anchor.y) < 1e-6;
  if (dxSame && dySame) return [];
  if (dxSame || dySame) return [[]]; // already axis-aligned with the anchor
  return [
    [{ x: anchor.x, y: head.y }], // horizontal leg first
    [{ x: head.x, y: anchor.y }], // vertical leg first
  ];
}

/**
 * Normalise a corner polyline into a legal Manhattan path (open) or loop
 * (closed): drop coincident vertices, merge collinear runs into a single
 * straight, and remove 180° reversal spikes. Returns `valid=false` when the
 * result cannot form a legal path/loop (e.g. a residual non-orthogonal turn), so
 * the caller can reject the connection rather than build illegal track.
 */
export function cleanupManhattanPolyline(input: Vec[], closed: boolean): CleanupResult {
  let v = input.map((p) => ({ ...p }));

  // Drop consecutive duplicates.
  const dedup: Vec[] = [];
  for (const p of v) {
    const last = dedup[dedup.length - 1];
    if (!last || Math.hypot(last.x - p.x, last.y - p.y) > 1e-6) dedup.push(p);
  }
  v = dedup;

  // For a closed loop, drop a trailing vertex coincident with the first.
  if (closed && v.length > 1) {
    const a = v[0];
    const b = v[v.length - 1];
    if (Math.hypot(a.x - b.x, a.y - b.y) <= 1e-6) v.pop();
  }
  if (v.length < 2) return { vertices: v, valid: false };

  const initialSegmentCount = closed ? v.length : v.length - 1;
  for (let i = 0; i < initialSegmentCount; i++) {
    if (!isAxisAligned(v[i], v[(i + 1) % v.length])) {
      return { vertices: v, valid: false };
    }
  }

  // Iteratively remove interior vertices that are collinear (merge straights) or
  // 180° reversals (spikes).
  let changed = true;
  let guard = 0;
  while (changed && guard++ < 2000 && v.length >= 3) {
    changed = false;
    const N = v.length;
    const lo = closed ? 0 : 1;
    const hi = closed ? N : N - 1;
    for (let i = lo; i < hi; i++) {
      const prev = v[(i - 1 + N) % N];
      const cur = v[i % N];
      const next = v[(i + 1) % N];
      const h1 = segHeading(prev, cur);
      const h2 = segHeading(cur, next);
      const turn = (((h2 - h1) % 360) + 360) % 360;
      if (h1 === h2 || turn === 180) {
        v.splice(i % N, 1);
        changed = true;
        break;
      }
    }
  }
  if (v.length < (closed ? 3 : 2)) return { vertices: v, valid: false };

  // Validate every interior corner is a clean ±90° turn.
  const N = v.length;
  const lo = closed ? 0 : 1;
  const hi = closed ? N : N - 1;
  for (let i = lo; i < hi; i++) {
    const prev = v[(i - 1 + N) % N];
    const cur = v[i % N];
    const next = v[(i + 1) % N];
    const turn = (((segHeading(cur, next) - segHeading(prev, cur)) % 360) + 360) % 360;
    if (turn !== 90 && turn !== 270) return { vertices: v, valid: false };
  }
  return { vertices: v, valid: true };
}
